import { DiscoveryAnswer, DiscoveryFormSection, DiscoveryQuestion } from '../../../../types/advise';
import { StepStatus } from '../EngagementSteps';

export interface DiscoveryProgress {
  questions: number;
  answered: number;
  required: number;
  requiredAnswered: number;
}

export interface DiscoverySectionProgress extends DiscoveryProgress {
  status: StepStatus;
}

/** What the form shows for a question: its saved answer, or its default until one is saved. */
export const shownAnswer = (question: DiscoveryQuestion): DiscoveryAnswer['answer'] =>
  question.value?.answer !== undefined ? question.value.answer : question.default;

/** The one rule every Discovery count uses; a pick-any question is answered once one option is ticked. */
export const isAnswered = (question: DiscoveryQuestion): boolean => {
  const answer = shownAnswer(question);
  switch (question.field_type) {
    case 'text':
    case 'notes':
    case 'select':
      return typeof answer === 'string' && answer.trim() !== '';
    case 'number':
      return typeof answer === 'number';
    case 'yes_no':
      return typeof answer === 'boolean';
    case 'multi_select':
      return Array.isArray(answer) && answer.length > 0;
  }
};

const countProgress = (questions: DiscoveryQuestion[]): DiscoveryProgress => {
  const required = questions.filter((question) => question.required);
  return {
    questions: questions.length,
    answered: questions.filter(isAnswered).length,
    required: required.length,
    requiredAnswered: required.filter(isAnswered).length
  };
};

const questionsStatus = (questions: DiscoveryQuestion[]): StepStatus => {
  const progress = countProgress(questions);
  // Questions with none required are Complete once every one is answered.
  const done =
    progress.required > 0 ? progress.requiredAnswered === progress.required : progress.answered === progress.questions;
  if (progress.questions > 0 && done) return 'complete';
  return progress.answered > 0 ? 'in_progress' : 'not_started';
};

export const sectionProgress = (section: DiscoveryFormSection): DiscoverySectionProgress => ({
  ...countProgress(section.questions),
  status: questionsStatus(section.questions)
});

// The step by the same rule as each of its sections, over every question in them.
export const discoveryStepStatus = (sections: DiscoveryFormSection[]): StepStatus =>
  questionsStatus(sections.flatMap((section) => section.questions));

export const discoveryProgress = (sections: DiscoveryFormSection[]): DiscoveryProgress =>
  countProgress(sections.flatMap((section) => section.questions));

export const firstIncompleteSection = (sections: DiscoveryFormSection[]): DiscoveryFormSection | undefined =>
  sections.find((section) => section.questions.some((question) => question.required && !isAnswered(question)));
